import React, {useState, useEffect} from 'react';
import EmployerDashboardNav from "../../employer/EmployerDashboardNav";
import {Formik, Form, Field, ErrorMessage} from 'formik';
import * as Yup from 'yup';
import axios from "axios";
import {toast} from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import {Modal} from "bootstrap";
import Breadcrumb from '../../Breadcrumb';

const ManageHr = () => {
  const [hrUsers, setHrUsers] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [loading, setLoading] = useState(true);
  const [editingUser, setEditingUser] = useState(null);
  const [confirmationModal, setConfirmationModal] = useState(null);
  const [activationModal, setActivationModal] = useState(null);
  const [selectedHr, setSelectedHr] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [modalStatus, setModalStatus] = useState('confirm');
  const [modalMessage, setModalMessage] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [errorMessage, setErrorMessage] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  const usersPerPage = 6;
  const indexOfLastUser = currentPage * usersPerPage;
  const indexOfFirstUser = indexOfLastUser - usersPerPage;
  const currentUsers = hrUsers.slice(indexOfFirstUser, indexOfLastUser);
  const totalPages = Math.ceil(hrUsers.length / usersPerPage);

  useEffect(() => {
    // Initialize delete confirmation modal
    const modalElement = document.getElementById('confirmationModal');
    const modal = new Modal(modalElement, {
      backdrop: 'static',
      keyboard: false
    });
    setConfirmationModal(modal);

    // Initialize activation modal
    const activationModalElement = document.getElementById('activationModal');
    const activationModalInstance = new Modal(activationModalElement, {
      backdrop: 'static',
      keyboard: false
    });
    setActivationModal(activationModalInstance);
  }, []);

  const validationSchema = Yup.object({
    name: Yup.string()
      .required('Name is required')
      .min(2, 'Name must be at least 2 characters'),
    email: Yup.string()
      .email('Invalid email format')
      .required('Email is required'),
    mobile: Yup.string()
      .matches(/^[0-9]{10}$/, 'Mobile number must be 10 digits')
      .required('Mobile number is required'),
//    password: Yup.string()
//     .min(8, 'Password must be at least 8 characters')
//     .matches(/[0-9]/, 'Password must contain at least one number')
//     .matches(/[A-Z]/, 'Password must contain at least one uppercase letter')
//     .matches(/[a-z]/, 'Password must contain at least one lowercase letter')
//     .matches(/[^A-Za-z0-9]/, 'Password must contain at least one special character')
//     .required('Password is required'),
    linkedinid: Yup.string()
      .url('Must be a valid LinkedIn URL'),
//    designation: Yup.string()
//      .required('Designation is required')
  });

  useEffect(() => {
    fetchHrUsers();
  }, []);

  const fetchHrUsers = async () => {
    try {
      const companyId = localStorage.getItem('companyId');
      const response = await axios.get(`${process.env.REACT_APP_API_URL}hr_details/showallhr_details/${companyId}`);
      setHrUsers(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching HR users:', error);
      setLoading(false);
    }
  };

  const handleEdit = (user) => {
    setEditingUser(user);
    setShowForm(true);
    window.scrollTo({top: 200, behavior: 'smooth'});
  };

  const handleDeleteHr = (hr) => {
    setSelectedHr(hr);
    setModalStatus('before-delete');
    setModalMessage('');
    confirmationModal.show();
  };

  const handleActivateHr = (hr) => {
    setSelectedHr(hr);
    setModalStatus('before-activation');
    setModalMessage(hr.isactive === 1 
      ? 'Are you sure you want to deactivate this HR user?' 
      : 'Are you sure you want to activate this HR user?');
    activationModal.show();
  };

  const confirmActivation = async () => {
    if (!selectedHr) return;

    setIsProcessing(true);
    setModalStatus('processing');

    try {
      const url = selectedHr.isactive === 1
        ? `${process.env.REACT_APP_API_URL}users/deactivateuser/${selectedHr.userid}`
        : `${process.env.REACT_APP_API_URL}users/activateuser/${selectedHr.userid}`;
      
      await axios.put(url);
      await fetchHrUsers();

      setModalStatus('success');
      setModalMessage(selectedHr.isactive === 1 
        ? 'HR user successfully deactivated!' 
        : 'HR user successfully activated!');
      setTimeout(() => {
        activationModal.hide();
        setTimeout(() => {
          setSelectedHr(null);
          setModalStatus('confirm');
          setModalMessage('');
          setIsProcessing(false);
        }, 300);
      }, 1200);
    } catch (error) {
      console.error('Error updating HR status:', error);
      setModalStatus('error');
      setModalMessage('An error occurred. Please try again.');
      setTimeout(() => {
        activationModal.hide();
        setTimeout(() => {
          setSelectedHr(null);
          setModalStatus('confirm');
          setModalMessage('');
          setIsProcessing(false);
        }, 300);
      }, 1200);
    }
  };

  const handleModalClose = () => {
    confirmationModal.hide();
    setTimeout(() => {
      setSelectedHr(null);
      setModalStatus('confirm');
      setModalMessage('');
      setIsProcessing(false);
    }, 300);
  };

  const handleActivationModalClose = () => {
    activationModal.hide();
    setTimeout(() => {
      setSelectedHr(null);
      setModalStatus('confirm');
      setModalMessage('');
      setIsProcessing(false);
    }, 300);
  };

  const deleteHr = async () => {
    if (!selectedHr) return;

    setIsProcessing(true);
    setModalStatus('processing');

    try {
      await axios.put(`${process.env.REACT_APP_API_URL}hr_details/deletehr/${selectedHr.userid}`);
      await fetchHrUsers();

      setModalStatus('success');
      setModalMessage('HR user successfully deleted!');
      setTimeout(handleModalClose, 1200);
    } catch (error) {
      console.error('Error deleting HR user:', error);
      setModalStatus('error');
      setModalMessage('An error occurred. Please try again.');
      setTimeout(handleModalClose, 1200);
    } finally {
      setIsProcessing(false);
    }
  };

  const paginate = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  const pageNumbers = [];
  for (let i = 1; i <= totalPages; i++) {
    pageNumbers.push(i);
  }

  const handleSubmit = async (values, {setSubmitting, resetForm}) => {
    try {
      const companyId = localStorage.getItem('companyId');
      const url = editingUser
        ? `${process.env.REACT_APP_API_URL}hr_details/updatehr`
        : `${process.env.REACT_APP_API_URL}hr_details/createhr`;

      const method = editingUser ? 'put' : 'post';
      const body = editingUser
        ? {...values, userid: editingUser.userid, company_id: companyId}
        : {...values, company_id: companyId};

      const response = await axios({
        url,
        method,
        headers: {
          'Content-Type': 'application/json',
        },
        data: body,
      });

      if (response.status === 200 || response.status === 201) {
        toast.success(
          editingUser
            ? 'HR user updated successfully'
            : 'HR user created successfully'
        );
        setShowForm(false);
        setEditingUser(null);
        setErrorMessage("");
        resetForm();
        fetchHrUsers();
      } else {
        new Error(response.data.message || 'Operation failed');
      }
    } catch (error) {
      if (error.response?.status === 400) {
        setErrorMessage(error.response.data.message);
      } else {
        setErrorMessage('Something went wrong. Please try again.');
      }
      setSubmitting(false);
    }
  };

  const renderModalContent = () => {
    switch (modalStatus) {
      case 'processing':
        return (
          <div className="loading-spinner-container">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Processing...</span>
            </div>
            <p className="mt-3">Processing your request...</p>
          </div>
        );

      case 'success':
        return (
          <div className="text-center text-success">
            <i className="fas fa-check-circle fa-3x mb-3"></i>
            <p>{modalMessage}</p>
          </div>
        );

      case 'error':
        return (
          <div className="text-center text-danger">
            <i className="fas fa-exclamation-circle fa-3x mb-3"></i>
            <p>{modalMessage}</p>
          </div>
        );

      case 'before-delete':
        return (
          <div className="text-center text-danger">
            <i className="fas fa-times fa-3x mb-3"></i>
            <p className="mb-0">
              Are you sure you want to delete this HR user?
            </p>
            <p className="fw-bold mt-2">"{selectedHr?.name}"</p>
          </div>
        );

      case 'before-activation':
        return (
          <div className="text-center">
            <i className={`fas fa-${selectedHr?.isactive === 1 ? 'user-slash' : 'user-check'} fa-3x mb-3`}></i>
            <p className="mb-0">{modalMessage}</p>
            <p className="fw-bold mt-2">"{selectedHr?.name}"</p>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <>
      {/* <div className="site-breadcrumb">
        <div className="container">
          <h2 className="breadcrumb-title">Manage HR</h2>
          <ul className="breadcrumb-menu">
            <li><a href="index.html">Home</a></li>
            <li className="active">Manage HR</li>
          </ul>
        </div>
        <div className="hero-shape">
          <img 
            loading="lazy"
            role="presentation"
            decoding="async"
            fetchpriority="high" className="hero-shape-1" src="assets/img/shape/03.svg" alt=""
          />
          <img 
            loading="lazy"
            role="presentation"
            decoding="async"
            fetchpriority="high" className="hero-shape-2" src="assets/img/shape/05.svg" alt=""
          />
          <img 
            loading="lazy"
            role="presentation"
            decoding="async"
            fetchpriority="high" className="hero-shape-3" src="assets/img/shape/06.svg" alt=""
          />
          <img 
            loading="lazy"
            role="presentation"
            decoding="async"
            fetchpriority="high" className="hero-shape-4" src="assets/img/shape/07.svg" alt=""
          />
        </div>
      </div> */}
      <Breadcrumb
        title="Manage HR"
        path={[
          { label: "Home", href: "/", active: false },
          { label: "Manage HR", active: true }
        ]}
      />
      <div className='user-profile py-120'>
        <div className='container'>
          <div className='row'>
            <EmployerDashboardNav/>
            <div className="col-lg-9">
              <div className="hr-management-section fade-in">
                <div className="d-flex justify-content-between align-items-center mb-4">
                  <h3 className="section-title">
                    {editingUser ? 'Edit HR User' : 'HR Management'}
                  </h3>
                  {!showForm && (
                    <button
                      className="btn btn-primary create-hr-btn"
                      onClick={() => setShowForm(true)}
                    >
                      Create HR User
                    </button>
                  )}
                </div>

                {showForm && (
                  <div className="hr-form-container slide-down">
                    <Formik
                      initialValues={editingUser ? {
                        name: editingUser.name,
                        email: editingUser.email,
                        mobile: editingUser.mobile,
                        password: editingUser.password,
                        linkedinid: editingUser.linkedinid || '',
                        designation: editingUser.designation
                      } : {
                        name: '',
                        email: '',
                        mobile: '',
                        password: '',
                        linkedinid: '',
                        designation: ''
                      }}
                      validationSchema={validationSchema}
                      onSubmit={handleSubmit}
                    >
                      {({isSubmitting, touched, errors}) => (
                        <Form className="hr-creation-form">
                          <div className="row">
                            <div className="col-md-6 mb-3">
                              <div className="form-group">
                                <label htmlFor="name" className="label">Full Name</label>
                                <Field
                                  type="text"
                                  name="name"
                                  className={`form-control ${touched.name && errors.name ? 'is-invalid' : ''}`}
                                  placeholder="Enter full name"
                                />
                                <ErrorMessage name="name" component="div" className="invalid-feedback"/>
                              </div>
                            </div>
                            <div className="col-md-6 mb-3">
                              <div className="form-group">
                                <label htmlFor="email">Email Address</label>
                                <Field
                                  type="email"
                                  name="email"
                                  className={`form-control ${touched.email && errors.email ? 'is-invalid' : ''}`}
                                  placeholder="Enter email address"
                                />
                                <ErrorMessage name="email" component="div" className="invalid-feedback"/>
                              </div>
                            </div>
                            <div className="col-md-6 mb-3">
                              <div className="form-group">
                                <label htmlFor="mobile">Mobile Number</label>
                                <Field
                                  type="text"
                                  name="mobile"
                                  className={`form-control ${touched.mobile && errors.mobile ? 'is-invalid' : ''}`}
                                  placeholder="Enter mobile number"
                                />
                                <ErrorMessage name="mobile" component="div" className="invalid-feedback"/>
                              </div>
                            </div>
                            <div className="col-md-6 mb-3">
                              <div className="form-group position-relative">
                                <label htmlFor="password">Password</label>

                                <div style={{ position: "relative" }}>
                                  <Field
                                    type={showPassword ? "text" : "password"}
                                    name="password"
                                    className={`form-control ${touched.password && errors.password ? 'is-invalid' : ''}`}
                                    placeholder="Enter password"
                                  />
                                  <span
                                    onClick={() => setShowPassword(prev => !prev)}
                                    style={{
                                      position: "absolute",
                                      top: "50%",
                                      right: "16px",
                                      transform: "translateY(-50%)",
                                      cursor: "pointer",
                                      color: "#666"
                                    }}
                                  >
                                    {showPassword ? (
                                      <i className="fa fa-eye-slash"></i>
                                    ) : (
                                      <i className="fa fa-eye"></i>
                                    )}
                                  </span>
                                </div>

                                <ErrorMessage name="password" component="div" className="invalid-feedback" />
                              </div>
                            </div>
                            <div className="col-md-6 mb-3">
                              <div className="form-group">
                                <label htmlFor="linkedinid">LinkedIn Profile URL</label>
                                <Field
                                  type="text"
                                  name="linkedinid"
                                  className={`form-control ${touched.linkedinid && errors.linkedinid ? 'is-invalid' : ''}`}
                                  placeholder="Enter LinkedIn profile URL"
                                />
                                <ErrorMessage name="linkedinid" component="div" className="invalid-feedback"/>
                              </div>
                            </div>
                            <div className="col-md-6 mb-3">
                              <div className="form-group">
                                <label htmlFor="designation">Designation</label>
                                <Field
                                  type="text"
                                  name="designation"
                                  className={`form-control ${touched.designation && errors.designation ? 'is-invalid' : ''}`}
                                  placeholder="Enter designation"
                                />
                                <ErrorMessage name="designation" component="div" className="invalid-feedback"/>
                              </div>
                            </div>
                          </div>
                          <div className="text-end mt-4">
                            {errorMessage && (
                              <div className="text-danger p-2 mb-2">{errorMessage}</div>
                            )}

                            <button
                              type="button"
                              className="btn btn-secondary me-2"
                              onClick={() => {
                                setShowForm(false);
                                setEditingUser(null);
                                setErrorMessage('');
                              }}
                            >
                              Cancel
                            </button>
                            <button
                              type="submit"
                              className="btn btn-primary"
                              disabled={isSubmitting}
                            >
                              {isSubmitting ? (
                                <>
                                  <span className="spinner-border spinner-border-sm me-2" role="status"
                                        aria-hidden="true"></span>
                                  {editingUser ? 'Updating...' : 'Creating...'}
                                </>
                              ) : editingUser ? 'Update HR User' : 'Create HR User'}
                            </button>
                          </div>
                        </Form>
                      )}
                    </Formik>
                  </div>
                )}

                {loading ? (
                  <div className="text-center py-5">
                    <div className="spinner-border text-primary" role="status">
                      <span className="visually-hidden">Loading...</span>
                    </div>
                  </div>
                ) : currentUsers.length > 0 ? (
                  <div className="table-responsive fade-in">
                    <table className="table hr-table">
                      <thead className="table-light">
                      <tr>
                        <th>Name</th>
                        <th>Email</th>
                        <th>Mobile</th>
                        <th>Designation</th>
                        <th>LinkedIn</th>
                        <th>Status</th>
                        <th>Actions</th>
                      </tr>
                      </thead>
                      <tbody>
                      {currentUsers.map((hr) => (
                        <tr key={hr.userid} className="table-row-hover">
                          <td><h6 className="job-title">{hr.name}</h6></td>
                          <td>{hr.email}</td>
                          <td>{hr.mobile}</td>
                          <td>{hr.designation}</td>
                          <td>
                            {hr.linkedinid && (
                              <a href={hr.linkedinid} target="_blank" rel="noopener noreferrer">
                                View Profile
                              </a>
                            )}
                          </td>
                          <td className='text-center'>
                            <button
                              className="border-0 outline-0 bg-transparent p-0"
                              onClick={() => handleActivateHr(hr)}
                            >
                              <span
                                className={`me-2 rounded-circle d-inline-block ${hr.isactive === 1 ? "bg-success" : "bg-danger"}`}
                                style={{width: "20px", height: "20px"}}
                              ></span>
                            </button>
                          </td>
                          <td className="d-flex">
                            <button
                              className="btn btn-sm btn-outline-primary me-2"
                              onClick={() => handleEdit(hr)}
                            >
                              Edit
                            </button>
                            <button
                              className="btn btn-sm btn-outline-danger"
                              onClick={() => handleDeleteHr(hr)}
                            >
                              Delete
                            </button>
                          </td>
                        </tr>
                      ))}
                      </tbody>
                    </table>
                    <div className="pagination-wrapper">
                      <div className="pagination-container">
                        <button
                          className="pagination-btn prev"
                          onClick={() => paginate(currentPage - 1)}
                          disabled={currentPage === 1}
                        >
                          <i className="far fa-chevron-left me-1"></i>
                          Previous
                        </button>

                        <div className="page-numbers">
                          {pageNumbers.map(number => (
                            <button
                              key={number}
                              className={`page-number ${currentPage === number ? 'active' : ''}`}
                              onClick={() => paginate(number)}
                            >
                              {number}
                            </button>
                          ))}
                        </div>

                        <button
                          className="pagination-btn next"
                          onClick={() => paginate(currentPage + 1)}
                          disabled={currentPage === totalPages}
                        >
                          Next
                          <i className="far fa-chevron-right ms-1"></i>
                        </button>
                      </div>

                      <div className="entries-info">
                        Showing {indexOfFirstUser + 1} to {Math.min(indexOfLastUser, hrUsers.length)} of {hrUsers.length} entries
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-5 no-hr-message fade-in">
                    <img 
                      loading="lazy"
                      role="presentation"
                      decoding="async"
                      fetchpriority="high" src="assets/img/empty-state.svg" alt="No HR Users" className="empty-state-img mb-3"
                    />
                    <h4>No HR Users Found</h4>
                    <p className="text-muted">Create your first HR user to get started</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="modal fade custom-modal" id="confirmationModal" tabIndex="-1"
           aria-labelledby="confirmationModalLabel" aria-hidden="true">
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-header border-bottom-0">
              <h5 className="modal-title" id="confirmationModalLabel">
                Delete HR User
              </h5>
              {!isProcessing && modalStatus === 'confirm' && (
                <button
                  type="button"
                  className="btn-close"
                  onClick={handleModalClose}
                  aria-label="Close"
                ></button>
              )}
            </div>
            <div className="modal-body text-center py-4">
              {renderModalContent()}
            </div>
            {modalStatus === 'before-delete' && !isProcessing && (
              <div className="modal-footer border-top-0 justify-content-center">
                <button
                  type="button"
                  className="btn btn-outline-secondary px-4"
                  onClick={handleModalClose}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-danger px-4"
                  onClick={deleteHr}
                >
                  Delete
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="modal fade custom-modal" id="activationModal" tabIndex="-1"
           aria-labelledby="activationModalLabel" aria-hidden="true">
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-header border-bottom-0">
              <h5 className="modal-title" id="activationModalLabel">
                {selectedHr?.isactive === 1 ? 'Deactivate HR User' : 'Activate HR User'}
              </h5>
              {!isProcessing && modalStatus === 'confirm' && (
                <button
                  type="button"
                  className="btn-close"
                  onClick={handleActivationModalClose}
                  aria-label="Close"
                ></button>
              )}
            </div>
            <div className="modal-body text-center py-4">
              {renderModalContent()}
            </div>
            {modalStatus === 'before-activation' && !isProcessing && (
              <div className="modal-footer border-top-0 justify-content-center">
                <button
                  type="button"
                  className="btn btn-outline-secondary px-4"
                  onClick={handleActivationModalClose}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className={`btn px-4 ${selectedHr?.isactive === 1 ? 'btn-danger' : 'btn-success'}`}
                  onClick={confirmActivation}
                >
                  {selectedHr?.isactive === 1 ? 'Deactivate' : 'Activate'}
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default ManageHr;